import { useMemo, useState, type FormEvent } from 'react';
import { Eye, EyeOff, LockKeyhole, Mail } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { Card, StatusCard } from '@/components/ui/card';
import { SectionLabel } from '@/components/ui/section-label';
import { useTranslation } from '@/hooks/useTranslation';

export type AuthLoginCardProps = {
  username: string;
  password: string;
  rememberMe: boolean;
  isSubmitting: boolean;
  isAuthInitialized: boolean;
  error?: string;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onUsernameChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onRememberMeChange: (value: boolean) => void;
};

export function AuthLoginCard({
  username,
  password,
  rememberMe,
  isSubmitting,
  isAuthInitialized,
  error,
  onSubmit,
  onUsernameChange,
  onPasswordChange,
  onRememberMeChange,
}: AuthLoginCardProps) {
  const [showPassword, setShowPassword] = useState(false);
  // AuthPage already shows a loading screen while !isAuthInitialized, so this
  // guard is a defensive layer for any future caller that skips that gate.
  const disabled = useMemo(
    () => !isAuthInitialized || isSubmitting || !username || !password,
    [isAuthInitialized, isSubmitting, password, username]
  );
  const { t } = useTranslation();

  return (
    <Card
      as="section"
      tier="hero"
      padding="none"
      className="flex h-full flex-col justify-between p-8"
    >
      <div className="space-y-8">
        <header className="space-y-3">
          <SectionLabel>{t('auth.login.badge')}</SectionLabel>
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold text-foreground">{t('auth.login.title')}</h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {t('auth.login.description')}
            </p>
          </div>
        </header>

        <form onSubmit={onSubmit} className="space-y-5">
          <div className="space-y-1.5">
            <label htmlFor="email" className="text-sm font-medium text-foreground">
              {t('auth.login.fields.email.label')}
            </label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                autoComplete="username"
                className="pl-10"
                placeholder={t('auth.login.fields.email.placeholder')}
                value={username}
                onChange={(event) => onUsernameChange(event.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="password" className="text-sm font-medium text-foreground">
              {t('auth.login.fields.password.label')}
            </label>
            <div className="relative">
              <LockKeyhole className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="password"
                placeholder={t('auth.login.fields.password.placeholder')}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className="pl-10 pr-10"
                value={password}
                onChange={(event) => onPasswordChange(event.target.value)}
                required
              />
              <IconButton
                type="button"
                icon={showPassword ? EyeOff : Eye}
                label={
                  showPassword
                    ? t('auth.login.fields.password.hide')
                    : t('auth.login.fields.password.show')
                }
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-0.5 top-1/2 -translate-y-1/2"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Checkbox
                id="remember"
                checked={rememberMe}
                onCheckedChange={(checked) => onRememberMeChange(checked === true)}
              />
              <label htmlFor="remember" className="text-xs text-muted-foreground">
                {t('auth.login.fields.remember')}
              </label>
            </div>
          </div>

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={disabled}
            loading={isSubmitting}
          >
            {isSubmitting ? t('auth.login.button.submitting') : t('auth.login.button.idle')}
          </Button>

          {error && <StatusCard type="error" message={error} className="text-sm" />}
        </form>
      </div>

      <Card
        as="footer"
        tier="muted"
        padding="none"
        className="mt-8 px-4 py-3 text-xs text-muted-foreground"
      >
        <p>{t('auth.login.footer.notice')}</p>
      </Card>
    </Card>
  );
}
